<template>
  <div class="aiChatDetails">
    <div class="aiChatDetails__header">
      <router-link
        class="aiChatDetails__back"
        to="/admin/ai-chat"
        title="Back to AI chat history"
        >
        <svg-icon name="left-arrow" />
      </router-link>

      <div class="aiChatDetails__header">
        <div class="aiChatDetails__title">{{ chat.title }}</div>
      </div>

      <div class="aiChatDetails__meta">
        <span v-if="chat.createdAtUtc">
            {{ formattedChatDate(chat.createdAtUtc) }}
        </span>
      </div>
    </div>

    <div
      v-if="loading"
      class="aiChatDetails__empty"
    >
      Loading...
    </div>

    <div
      v-else-if="error"
      class="aiChatDetails__empty"
    >
      {{ error }}
    </div>

    <div
      v-else
      ref="messagesContainer"
      class="aiChatDetails__messages"
    >
      <div
        v-for="message in messages"
        :key="message.id"
        :class="['message-row', message.role]"
      >
        <div class="message-bubble">
          {{ message.content }}
        </div>

        <div
          v-if="message.role === 'assistant' && message.flights.length"
          class="flights-section"
        >
          <div class="flights-list">
            <div
              v-for="(flight, idx) in message.flights"
              :key="flight.aircraftAvailabilityId + idx"
              class="flight-card"
            >
              <div
                v-if="flight.bookingType === 16"
                class="space-badge space-badge--empty-full"
              >
                EMPTY LEG
              </div>

              <div
                v-else-if="flight.bookingType === 32"
                class="space-badge space-badge--empty"
              >
                EMPTY LEG SPACE
              </div>

              <div
                v-else-if="flight.bookingType === 4"
                class="space-badge"
              >
                CHARTER SPACE
              </div>

              <div
                v-else-if="flight.bookingType === 1024"
                class="space-badge"
              >
                EXCHANGE SPACE
              </div>

              <div
                v-else-if="[512, 2048].includes(flight.bookingType)"
                class="space-badge"
              >
                EXCHANGE
              </div>

              <div
                v-else-if="flight.bookingType === 2"
                class="space-badge space-badge--private"
              >
                PRIVATE CHARTER
              </div>

              <div class="flight-card__header">
                <div class="route-info">
                  <div class="route-point">
                    <span class="label">FROM</span>
                    <span class="value">
                      {{ flight.departureAirportId }}
                    </span>
                  </div>

                  <div class="route-point">
                    <span class="label">TO</span>
                    <span class="value">
                      {{ flight.arrivalAirportId }}
                    </span>
                  </div>
                </div>

                <div class="route-point date-point">
                  <span class="label">DATE</span>
                  <span class="value">
                    {{ formattedDate(flight.departureDate) }}
                  </span>
                </div>
              </div>

              <div class="flight-card__body">
                <div
                  v-if="flight.defaultImageUrl"
                  class="flight-card__image"
                >
                  <img :src="flight.defaultImageUrl" />
                </div>

                <div class="flight-card__main-info">
                  <div class="info-group">
                    <span class="label">Aircraft:</span>
                    <span class="model-name">
                      {{ flight.aircraftModel }}
                    </span>
                  </div>
                </div>

              </div>

              <div class="flight-card__specs">
                <div
                  v-for="spec in getFlightSpecs(flight)"
                  :key="spec.label"
                  class="spec-item"
                  :class="{ 'spec-item--price': spec.isPrice }"
                >
                  <span class="label">{{ spec.label }}:</span>
                  <span class="value">{{ spec.value }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { aiChatAPI } from '@/api/ai-chat'
import SvgIcon from '@/components/common/SvgIcon/SvgIcon'

export default {
  name: 'AiChatDetails',

  data () {
    return {
      loading: false,
      error: null,

      chat: {
        id: null,
        title: '',
        createdAtUtc: '',
        updatedAtUtc: '',
        messages: [],
        searchResults: [],
      },

      messages: [],
    }
  },

  async mounted () {
    await this.loadChat()
  },

  methods: {
    async loadChat () {
      this.loading = true
      this.error = null

      try {
        const result = await aiChatAPI.getAdminHistoryDetails(
          this.$route.params.id,
        )

        this.chat = result.data
        this.buildMessages()
      } catch (e) {
        console.error('Failed to load AI chat details:', e)
        this.error = 'Failed to load chat.'
      } finally {
        this.loading = false
      }
    },

    buildMessages () {
      const searchResultsByMessageId = new Map()

      for (const searchResult of this.chat.searchResults || []) {
        const results =
          searchResultsByMessageId.get(searchResult.messageId) || []

        results.push(searchResult)
        searchResultsByMessageId.set(
          searchResult.messageId,
          results,
        )
      }

      this.messages = (this.chat.messages || []).map(message => {
        if (message.role === 'user') {
          return {
            ...message,
            flights: [],
          }
        }

        const messageResults =
          searchResultsByMessageId.get(message.id) || []

        const flights = messageResults.flatMap(searchResult =>
          this.getFlightsFromSearchData(searchResult.result),
        )

        return {
          ...message,
          flights,
        }
      })
    },

    getFlightsFromSearchData (searchData) {
      const flights = [
        ...(searchData?.emptyLegs || []).map(f =>
          this.normalizeFlight(f, 'empty-leg'),
        ),

        ...(searchData?.spaces || []).map(f =>
          this.normalizeFlight(
            f,
            f.emptyLegId ? 'empty-leg-space' : 'jet-space',
          ),
        ),

        ...(searchData?.charterAircrafts?.aircraftAvailabilities || []).map(
          f => this.normalizeFlight(f, 'charter'),
        ),

        ...(searchData?.charterAircrafts?.emptyLegs || []).map(f =>
          this.normalizeFlight(f, 'empty-leg'),
        ),

        ...(searchData?.charterSpaces?.aircraftAvailabilities || []).map(
          f => this.normalizeFlight(f, 'charter-space'),
        ),

        ...(searchData?.charterSpaces?.emptyLegs || []).map(f =>
          this.normalizeFlight(f, 'empty-leg-space'),
        ),
      ]

      flights.sort(
        (a, b) => this.getCalculatePrice(a) - this.getCalculatePrice(b),
      )

      return flights
    },

    normalizeFlight (flight, source) {
      return {
        ...flight,
        _uiSource: source,

        aircraftAvailabilityId:
          flight.aircraftAvailabilityId ||
          flight.emptyLegId ||
          `temp-${Math.random()}`,

        departureAirportId:
          flight.departureAirportId ||
          flight.departureCode ||
          'N/A',

        arrivalAirportId:
          flight.arrivalAirportId ||
          flight.arrivalCode ||
          'N/A',

        pax: flight.pax || 1,

        bookingType:
          flight.bookingType ||
          (source.includes('empty')
            ? 4
            : (source.includes('space') ? 32 : 1)),
      }
    },

    getCalculatePrice (f) {
      const type = Number(f.bookingType)

      if (type === 32) {
        return (f.exclusiveTotalPrice || 0) * (f.pax || 1)
      }

      if (type === 1024) {
        return f.pricePerHour || 0
      }

      return f.exclusiveTotalPrice || 0
    },

    getTotalPrice (item) {
      if (item.bookingType === 1024) {
        return (item.exclusiveTotalPrice || 0) * (item.pax || 1)
      }

      return item.exclusiveTotalPrice
    },

    getFlightSpecs (f) {
      const calculatedPrice = this.getTotalPrice(f)

      let paxValue = ''
      const isSpaceBooking = [4, 32, 1024].includes(f.bookingType)

      if (isSpaceBooking) {
        paxValue = `${f.aircraftPax} Space${f.aircraftPax > 1 ? 's' : ''}`

        if (f.spacesReserved) {
          paxValue += ` (${f.spacesReserved} reserved)`
        }
      } else {
        if (
          f.providerId !==
          'e5d2cc3b-6370-4fcf-54b8-08de1b9dd9b8'
        ) {
          paxValue =
            `Max ${f.passengersText || f.aircraftPax || 'N/A'}`
        } else {
          paxValue = `${f.aircraftPax || 'N/A'}`
        }
      }

      const specs = [
        { label: 'Pax', value: paxValue },
        {
          label: 'Range',
          value: f.aircraftRange
            ? `${f.aircraftRange} NM`
            : 'N/A',
        },
        {
          label: 'Category',
          value: f.aircraftType || 'N/A',
        },
        {
          label: 'Speed',
          value: f.aircraftSpeed
            ? `${f.aircraftSpeed} MPH`
            : 'N/A',
        },
        {
          label: 'Price',
          value: this.formatCurrency(calculatedPrice),
          isPrice: true,
        },
      ]

      return specs.filter(spec => spec.value !== 'N/A')
    },

    formattedDate (
      dateString,
      isDay = true,
      flightDurationHours = 0,
      flightDurationMinutes = 0,
    ) {
      const [datePart] = dateString.split('T')
      const [year, month, day] = datePart.split('-').map(Number)

      const date = new Date(year, month - 1, day)

      if (flightDurationHours || flightDurationMinutes) {
        date.setMinutes(
          date.getMinutes() +
          flightDurationHours * 60 +
          flightDurationMinutes,
        )
      }

      if (isDay) {
        const monthName = date.toLocaleDateString('en-US', {
          month: 'long',
        })

        return `${monthName}, ${date.getDate()}`
      }

      return date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      })
    },

    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(value || 0)
    },

    formattedChatDate (dateString) {
      if (!dateString) {
        return ''
      }

      const date = new Date(dateString)

      if (Number.isNaN(date.getTime())) {
        return ''
      }

      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    },
  },

  components: {
    SvgIcon,
  },
}
</script>

<style lang="stylus" scoped>
@import './AiChatDetails.styl'
</style>